// metaMarksHelpers.js
// Helper functions for working with overlapping meta marks (comments, references, tasks)

import {generateUUID} from "./utils";

/**
 * Add a meta mark to the current selection
 * @param {EditorView} view - ProseMirror editor view
 * @param {string} markType - 'comment', 'reference', or 'task'
 * @param {string} uid - Optional UID, will generate if not provided
 * @returns {string} The UID of the created mark
 */
export function addMetaMark(view, markType, uid = null) {
    const {state, dispatch} = view;
    const {from, to} = state.selection;

    if (from === to) {
        console.warn("Cannot add meta mark to empty selection");
        return null;
    }

    const mark = state.schema.marks[markType];
    if (!mark) {
        console.error(`Mark type "${markType}" not found in schema`);
        return null;
    }

    const metaUid = uid || generateUUID();
    const tr = state.tr.addMark(from, to, mark.create({uid: metaUid}));
    console.log(tr)
    dispatch(tr);

    return metaUid;
}

/**
 * Create a reference mark in ProseMirror.
 * The mark's metadata in Y.Map is initialised lazily when the first reference
 * is added via addReference() — no Firestore write needed here.
 *
 * @param {EditorView} view - ProseMirror editor view
 * @param {string} reportId - (unused, kept for call-site compatibility)
 * @param {string} uid - Optional UID, will generate if not provided
 * @returns {Promise<string|null>} The UID of the created mark, or null if failed
 */
export async function createReferenceMarkWithFirestore(view, reportId, uid = null) {
    const {state} = view;
    const {from, to} = state.selection;

    if (from === to) {
        console.warn("Cannot add reference mark to empty selection");
        return null;
    }

    const metaUid = addMetaMark(view, 'reference', uid);
    return metaUid || null;
}

/**
 * Remove a specific meta mark by UID from the selection or entire document
 * @param {EditorView} view - ProseMirror editor view
 * @param {string} markType - 'comment', 'reference', or 'task'
 * @param {string} uid - The UID of the mark to remove
 */
export function removeMetaMarkByUid(view, markType, uid) {
    const {state, dispatch} = view;
    const mark = state.schema.marks[markType];

    if (!mark) {
        console.error(`Mark type "${markType}" not found in schema`);
        return;
    }

    const tr = state.tr;
    let removed = false;

    state.doc.descendants((node, pos) => {
        if (!node.isText) return;

        node.marks.forEach((m) => {
            if (m.type === mark && m.attrs.uid === uid) {
                tr.removeMark(pos, pos + node.nodeSize, m);
                removed = true;
            }
        });
    });

    if (removed) {
        dispatch(tr);
    }
}

/**
 * Get all meta marks at the current caret position
 * Best practice: Use this when selection is collapsed (caret, not range)
 * @param {EditorState} state - ProseMirror editor state
 * @returns {Object} Object with arrays of UIDs for each mark type at caret
 */
export function getMetaMarksAtCaret(state) {
    const {$from, empty} = state.selection;

    if (!empty) {
        console.warn('Selection is not empty. Use getMetaMarksInSelection() for ranges.');
        return {comments: [], references: [], tasks: []};
    }

    // Get marks at the caret position
    const marks = state.storedMarks || $from.marks();

    const result = {
        comments: [],
        references: [],
        tasks: [],
    };

    marks.forEach((mark) => {
        if (mark.type === state.schema.marks.comment) {
            result.comments.push(mark.attrs.uid);
        } else if (mark.type === state.schema.marks.reference) {
            result.references.push(mark.attrs.uid);
        } else if (mark.type === state.schema.marks.task) {
            result.tasks.push(mark.attrs.uid);
        }
    });

    return result;
}

/**
 * Get all meta marks in the current selection
 * Only includes marks whose actual range intersects with the selection start and end
 * @param {EditorState} state - ProseMirror editor state
 * @returns {Object} Object with arrays of UIDs for each mark type
 */
export function getMetaMarksInSelection(state) {
    const {from, to} = state.selection;
    const result = {
        comments: new Set(),
        references: new Set(),
        tasks: new Set(),
    };

    state.doc.nodesBetween(from, to, (node, pos) => {
        if (!node.isText) return;

        const nodeStart = pos;
        const nodeEnd = pos + node.nodeSize;

        // Check if this node's range actually intersects with the selection
        // (nodesBetween may include nodes that are only partially in range)
        if (nodeEnd <= from || nodeStart >= to) return;

        node.marks.forEach((mark) => {
            if (mark.type === state.schema.marks.comment) {
                result.comments.add(mark.attrs.uid);
            } else if (mark.type === state.schema.marks.reference) {
                result.references.add(mark.attrs.uid);
            } else if (mark.type === state.schema.marks.task) {
                result.tasks.add(mark.attrs.uid);
            }
        });
    });

    return {
        comments: Array.from(result.comments),
        references: Array.from(result.references),
        tasks: Array.from(result.tasks),
    };
}

/**
 * Check if the current selection exactly matches an existing meta mark's range
 * Only returns marks where the selection boundaries exactly match the mark's boundaries
 * @param {EditorState} state - ProseMirror editor state
 * @param {string} markType - 'comment', 'reference', or 'task'
 * @returns {string|null} The UID of the exactly matching mark, or null if none match
 */
export function getExactlyMatchingMetaMark(state, markType) {
    const {from, to} = state.selection;

    if (from === to) {
        return null; // No selection
    }

    const mark = state.schema.marks[markType];
    if (!mark) return null;

    // Get all marks of this type in the selection
    const marksInSelection = new Set();
    state.doc.nodesBetween(from, to, (node, pos) => {
        if (!node.isText) return;

        const nodeStart = pos;
        const nodeEnd = pos + node.nodeSize;

        if (nodeEnd <= from || nodeStart >= to) return;

        node.marks.forEach((m) => {
            if (m.type === mark) {
                marksInSelection.add(m.attrs.uid);
            }
        });
    });

    // For each mark found, check if its range exactly matches the selection
    for (const uid of marksInSelection) {
        const ranges = getMetaMarkRanges(state, markType, uid);

        // Check if any range exactly matches the selection
        for (const range of ranges) {
            if (range.from === from && range.to === to) {
                return uid;
            }
        }
    }

    return null;
}

/**
 * Get ALL meta marks (from caret or selection)
 * @param {EditorState} state - ProseMirror editor state
 * @returns {Object} Object with arrays of UIDs for each mark type
 */
export function getMetaMarks(state) {
    const {empty} = state.selection;
    return empty ? getMetaMarksAtCaret(state) : getMetaMarksInSelection(state);
}

/**
 * Get all positions (ranges) where a specific meta mark UID appears
 * @param {EditorState} state - ProseMirror editor state
 * @param {string} markType - 'comment', 'reference', or 'task'
 * @param {string} uid - The UID to search for
 * @returns {Array<{from: number, to: number}>}
 */
export function getMetaMarkRanges(state, markType, uid) {
    const mark = state.schema.marks[markType];
    if (!mark) return [];

    const ranges = [];
    let currentRange = null;

    state.doc.descendants((node, pos) => {
        if (!node.isText) return;

        const hasMark = node.marks.some(
            (m) => m.type === mark && m.attrs.uid === uid
        );

        if (hasMark) {
            if (currentRange && currentRange.to === pos) {
                // Extend current range
                currentRange.to = pos + node.nodeSize;
            } else {
                // Start new range
                currentRange = {from: pos, to: pos + node.nodeSize};
                ranges.push(currentRange);
            }
        }
    });

    return ranges;
}

